import { type ReactNode } from "react";
import { XIcon } from "lucide-react";

import {
  AnimatedTabs,
  type AnimatedTabItem,
} from "@/components/atoms/AnimatedTabs";
import { Icon } from "@/components/atoms/Icon";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";
import { typographyStyles } from "@/components/ui/typography";

export function StripTabCloseButton({
  label,
  isSelected,
  onClose,
}: {
  label: string;
  isSelected: boolean;
  onClose: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="icon-xs"
      className={cn(
        "animated-tab-close",
        isSelected ? "opacity-[0.7]" : "opacity-[0]",
        "shrink-0 hover:opacity-[1]",
      )}
      aria-label={`Close ${label}`}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      {/* The close x of the filter and sort chips, at their weight. */}
      <Icon ReactIcon={XIcon} size="md" />
    </Button>
  );
}

type StripTabBarProps = {
  tabs: AnimatedTabItem[];
  selectedTabId: string;
  onTabChange: (tabId: string) => void;
  reorderable?: boolean;
  onTabsReorder?: (orderedTabIds: string[]) => void;
  trailingAction?: ReactNode;
  /** Enable horizontal scroll without a trailing action (e.g. public share header). */
  scrollable?: boolean;
};

/** Shared strip tab bar (threads, report layout groups, etc.). */
export function StripTabBar({
  tabs,
  selectedTabId,
  onTabChange,
  reorderable = false,
  onTabsReorder,
  trailingAction,
  scrollable = false,
}: StripTabBarProps) {
  return (
    <AnimatedTabs
      variant="strip"
      align="start"
      size="md"
      offset={0}
      tabStyle={typographyStyles["body-md"]}
      selectedTabId={selectedTabId}
      onTabChange={onTabChange}
      tabs={tabs}
      reorderable={reorderable}
      onTabsReorder={onTabsReorder}
      trailingAction={trailingAction}
      scrollable={scrollable}
    />
  );
}
